import React from 'react';
import { Tag, Car, Radio, Lightbulb, Projector, Bot, Calendar } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';

interface DisplayDetailsCardProps {
  yearStarted: number | null;
  fmStation: string | null;
  radioStation: string | null;
  parkingInfo: string | null;
  songCount: number;
  lightCount?: number | null;
  projectorUsed?: boolean;
  illumibotUsed?: boolean;
  displayType?: string;
  lastConfirmed?: string | null;
}

const DisplayDetailsCard: React.FC<DisplayDetailsCardProps> = ({
  yearStarted,
  fmStation,
  radioStation,
  parkingInfo,
  songCount,
  lightCount,
  projectorUsed,
  illumibotUsed,
  displayType,
  lastConfirmed
}) => {
  // Helper function to format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Helper function to format the last confirmed date
  const formatLastConfirmed = (dateString: string | null) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.warn('Error formatting last confirmed date:', error);
      return null;
    }
  };
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center">
          <Tag className="mr-2" size={18} />
          Display Details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {yearStarted && (
            <div>
              <div className="text-sm text-muted-foreground mb-1">Running Since</div>
              <div className="font-medium">{yearStarted}</div>
            </div>
          )}

          {lastConfirmed && formatLastConfirmed(lastConfirmed) && (
            <div>
              <div className="text-sm text-muted-foreground mb-1 flex items-center">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span>Last Confirmed</span>
              </div>
              <div className="font-medium">{formatLastConfirmed(lastConfirmed)}</div>
            </div>
          )}

          {lightCount && lightCount > 0 && (
            <div>
              <div className="text-sm text-muted-foreground mb-1 flex items-center">
                <Lightbulb className="h-3.5 w-3.5 mr-1" />
                <span>Number of Lights</span>
              </div>
              <div className="font-medium">{formatNumber(lightCount)} lights</div>
            </div>
          )}

          {displayType === 'Projection Show' && (projectorUsed || illumibotUsed) && (
            <div>
              <div className="text-sm text-muted-foreground mb-1">Projection Equipment</div>
              <div className="space-y-1">
                {projectorUsed && (
                  <div className="font-medium flex items-center">
                    <Projector className="h-3.5 w-3.5 mr-1" />
                    <span>Projector</span>
                  </div>
                )}
                {illumibotUsed && (
                  <div className="font-medium flex items-center">
                    <Bot className="h-3.5 w-3.5 mr-1" />
                    <span>Illumibot</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {(fmStation || radioStation) && (
            <div>
              <div className="text-sm text-muted-foreground mb-1 flex items-center">
                <Radio className="h-3.5 w-3.5 mr-1" />
                <span>Tune Radio To</span>
              </div>
              <div className="font-medium">
                {radioStation ? `${radioStation} FM` : fmStation}
              </div>
            </div>
          )}
          
          {parkingInfo && (
            <div>
              <div className="text-sm text-muted-foreground mb-1 flex items-center">
                <Car className="h-3.5 w-3.5 mr-1" />
                <span>Parking Info</span>
              </div>
              <div className="font-medium">{parkingInfo}</div>
            </div>
          )}
          
          <div>
            <div className="text-sm text-muted-foreground mb-1">Songs in Show</div>
            <div className="font-medium">{songCount} songs</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DisplayDetailsCard;
